//frontend/src/components/api/requestSelection/requestSelectionApi.js

import { authFetch } from "../api";
import { getCurrentUser } from "../auth";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.client_manager_id) qs.set("client_manager_id", params.client_manager_id);
  if (params.search_manager_id) qs.set("search_manager_id", params.search_manager_id);
  if (params.search) qs.set("search", params.search);
  if (params.limit != null) qs.set("limit", params.limit);
  if (params.offset != null) qs.set("offset", params.offset);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

// ---------- Заявки ----------

export async function getRequestSelections(params = {}) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection/list/${buildQuery(params)}`
  );
  if (!res.ok) {
    throw new Error(`Не удалось загрузить заявки: HTTP ${res.status}`);
  }
  const json = await res.json();
  const items = Array.isArray(json.data) ? json.data : [];
  items.total = json.total || 0;
  return items;
}

export async function getRequestSelection(id) {
  const res = await authFetch(`${BASE_URL}/api/requestselection/${id}/retrieve/`);
  if (!res.ok) throw new Error(`Не удалось загрузить заявку: HTTP ${res.status}`);
  const json = await res.json();
  return json.data;
}

export async function createRequestSelection(payload) {
  const res = await authFetch(`${BASE_URL}/api/requestselection/create/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Не удалось создать: HTTP ${res.status}`);
  }
  return (await res.json()).data;
}

export async function updateRequestSelection(id, payload) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection/${id}/partial-update/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Не удалось обновить: HTTP ${res.status}`);
  }
  return (await res.json()).data;
}

export async function deleteRequestSelection(id) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection/${id}/delete/`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error(`Не удалось удалить: HTTP ${res.status}`);
  return true;
}

export const getRequestSelectionById = getRequestSelection;

// ---------- Комментарии ----------

export async function getComments(requestId) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection-comment/?request=${requestId}`
  );
  if (!res.ok) return [];
  return await res.json();
}

export async function createComment(requestId, text) {
  const res = await authFetch(`${BASE_URL}/api/requestselection-comment/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ request: requestId, text }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Не удалось создать комментарий: HTTP ${res.status}`);
  }
  return await res.json();
}

export async function updateComment(id, text) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection-comment/${id}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    }
  );
  if (!res.ok) throw new Error(`Не удалось обновить: HTTP ${res.status}`);
  return await res.json();
}

export async function deleteComment(id) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection-comment/${id}/`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error(`Не удалось удалить: HTTP ${res.status}`);
  return true;
}

// ---------- Документы ----------

export async function getDocuments(requestId) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection-document/?request=${requestId}`
  );
  if (!res.ok) return [];
  return await res.json();
}

export async function uploadDocument({ request, file, name, comment }) {
  const fd = new FormData();
  fd.append("request", request);
  fd.append("file", file);
  if (name) fd.append("name", name);
  if (comment) fd.append("comment", comment);

  const res = await authFetch(`${BASE_URL}/api/requestselection-document/`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Не удалось загрузить: HTTP ${res.status}`);
  }
  return await res.json();
}

export async function updateDocument(id, { name, comment }) {
  const fd = new FormData();
  if (name != null) fd.append("name", name);
  if (comment != null) fd.append("comment", comment);

  const res = await authFetch(
    `${BASE_URL}/api/requestselection-document/${id}/`,
    { method: "PATCH", body: fd }
  );
  if (!res.ok) throw new Error(`Не удалось обновить: HTTP ${res.status}`);
  return await res.json();
}

export async function deleteDocument(id) {
  const res = await authFetch(
    `${BASE_URL}/api/requestselection-document/${id}/`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error(`Не удалось удалить: HTTP ${res.status}`);
  return true;
}

// ---------- События ----------

export async function getEvents(requestId) {
  const res = await authFetch(
    `${BASE_URL}/api/events/?request_selection=${requestId}`
  );
  if (!res.ok) return [];
  return await res.json();
}

export async function createEvent(payload) {
  const res = await authFetch(`${BASE_URL}/api/events/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(JSON.stringify(err));
  }
  return await res.json();
}

export async function updateEvent(id, payload) {
  const res = await authFetch(`${BASE_URL}/api/events/${id}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Не удалось обновить: HTTP ${res.status}`);
  return await res.json();
}

export async function deleteEvent(id) {
  const res = await authFetch(`${BASE_URL}/api/events/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Не удалось удалить: HTTP ${res.status}`);
  return true;
}

// ---------- Вспомогательные ----------

// getCurrentUser живёт в ../auth (там логика токена и dev-no-auth).
// Здесь — только реэкспорт, чтобы существующие импорты
// (AdminRequestDetail, AdminRequestsList, RequestSearchDetailPage)
// продолжали работать и не было двух разных реализаций.
export { getCurrentUser };

export async function listUsers() {
  const res = await authFetch(`${BASE_URL}/api/users/`);
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function listClients() {
  const res = await authFetch(`${BASE_URL}/api/clients/`);
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}